import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The one search field in the product.
 *
 * IT WAS THREE COPIES OF ITSELF. The Loft's row, the Feed's row and — once the
 * destination picker grew a filter — the composer's menu all needed a magnifier, a
 * borderless input, a clear button and the same focus ring, and two of them were
 * already byte-for-byte identical with a comment on each saying so ("IDENTICAL TO
 * THE LOFT'S, deliberately"). A comment is not a mechanism: the moment a third
 * caller appeared the three were going to drift about ring, padding and icon size,
 * which is the drift CLAUDE.md's consistency rule exists to stop. One component,
 * two shapes.
 *
 * WHAT IS NOT IN HERE IS WHAT IT SEARCHES. Every field names its own subject —
 * *Search spaces*, *Search your correspondence* — because "Search" alone invites a
 * handle and returns nothing, which reads as a product hiding people rather than
 * one that has no such surface (docs/5 § D260730-2028). So the label and the
 * placeholder are required props and are always the same sentence.
 *
 * `pill` IS THE TOP-ROW SHAPE: 40px, which is what sets the height of every tab's
 * opening row (see `TopRow`) — a shorter field here breaks the rhyme between the
 * three tabs. `bare` is the shape a field takes INSIDE a menu, where a filled pill
 * would read as a widget sitting on the menu rather than as its header.
 */
export function SearchField({
  value,
  onValue,
  label,
  placeholder,
  variant = 'pill',
  onClear,
  clearLabel = 'Clear the search',
  className,
  inputRef,
  ...input
}: {
  value: string;
  onValue: (value: string) => void;
  /** What it searches, in words. Both the `aria-label` and the placeholder. */
  label: string;
  placeholder?: string;
  variant?: 'pill' | 'bare';
  /**
   * What the trailing X does, when it has more to do than empty the field.
   *
   * PASSED BY THE FEED AND NOBODY ELSE, because the Feed's field is the only one
   * whose panel covers the screen it sits on: an empty field there still has
   * something to dismiss, so the button is drawn even with nothing typed. Every
   * other caller leaves this out and gets the ordinary behaviour — a clear button
   * that appears when there is something to clear.
   */
  onClear?: () => void;
  clearLabel?: string;
  inputRef?: React.Ref<HTMLInputElement>;
} & Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'placeholder' | 'type' | 'ref'
>) {
  const pill = variant === 'pill';
  const showClear = Boolean(onClear) || value.length > 0;

  return (
    <div
      className={cn(
        'relative flex min-w-0 items-center transition-shadow',
        pill
          ? 'h-10 flex-1 rounded-full bg-muted focus-within:ring-3 focus-within:ring-ring/50'
          : 'h-9 w-full border-b border-border',
        className,
      )}
    >
      <Search
        aria-hidden
        className={cn(
          'pointer-events-none absolute size-4 text-muted-foreground',
          pill ? 'start-3.5' : 'start-2',
        )}
      />
      <input
        ref={inputRef}
        /* `type="search"` for the keyboard's own Search key on iOS; the platform's
           cancel button is hidden because the one below is the app's. */
        type="search"
        value={value}
        onChange={(e) => onValue(e.target.value)}
        aria-label={label}
        placeholder={placeholder ?? label}
        className={cn(
          'h-full w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden',
          pill ? 'pe-9 ps-10' : 'pe-8 ps-8',
        )}
        {...input}
      />
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          /* TWO STEPS, NEVER ONE. With something typed the X empties the field and
             leaves you in the search, because clearing a query is how you start
             another one; with the field already empty there is nothing left to
             clear and the same button dismisses the panel. One tap, one outcome,
             and the label says which. */
          onClick={() => (value ? onValue('') : onClear?.())}
          aria-label={value ? clearLabel : 'Close the search'}
          className={cn(
            'absolute rounded-full text-muted-foreground',
            pill ? 'end-1' : 'end-0.5',
          )}
        >
          <X aria-hidden />
        </Button>
      )}
    </div>
  );
}
